import { lazy, Suspense, Component, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/auth'
import { PreviewRoleProvider } from './hooks/usePreviewRole'
import { useRole } from './hooks/useRole'
import { ToastProvider } from './components/ui/Toast'
import { PageLoading } from './components/ui/LoadingSpinner'
import AppShell from './components/layout/AppShell'
import PlatformShell from './components/layout/PlatformShell'

// Core (always loaded)
import LoginPage from './pages/LoginPage'
import SetPasswordPage from './pages/SetPasswordPage'
import NotFoundPage from './pages/NotFoundPage'
import DeactivatedPage from './pages/DeactivatedPage'

// Dashboard lazy-loaded like all other routes
const DashboardPage = lazy(() => import('./modules/dashboard/DashboardPage'))
// GrantsPage is now a tab inside FinancesPage — imported there directly.

// Lazy-loaded routes — code-split into chunks
const PublicShell = lazy(() => import('./pages/public/PublicShell'))
const AdoptListPage = lazy(() => import('./pages/public/AdoptListPage'))
const AdoptAnimalPage = lazy(() => import('./pages/public/AdoptAnimalPage'))
const ApplicationForm = lazy(() => import('./pages/public/ApplicationForm'))
const FosterApplicationPage = lazy(() => import('./modules/foster-applications/FosterApplicationPage'))
const AdoptionApplicationPage = lazy(() => import('./modules/adoption-applications/AdoptionApplicationPage'))
const AdoptionSuccessPage = lazy(() => import('./modules/adoption-applications/AdoptionSuccessPage'))
const ClaimApplicationPage = lazy(() => import('./pages/public/ClaimApplicationPage'))
const ConfirmClaimPage = lazy(() => import('./pages/public/ConfirmClaimPage'))
const ApplicationsPage = lazy(() => import('./modules/applications/ApplicationsPage'))
const ApplicationDetailPage = lazy(() => import('./modules/applications/ApplicationDetailPage'))
const AdoptionApplicationDetailPage = lazy(() => import('./modules/applications/AdoptionApplicationDetailPage'))
const AdopterShell = lazy(() => import('./modules/adopter/AdopterShell'))
const DashboardApplicationsPage = lazy(() => import('./modules/adopter/DashboardApplicationsPage'))
const DashboardApplicationDetailPage = lazy(() => import('./modules/adopter/DashboardApplicationDetailPage'))
const DashboardSettingsPage = lazy(() => import('./modules/adopter/DashboardSettingsPage'))
const InboxPage = lazy(() => import('./modules/inbox/InboxPage'))
const InboxAnalytics = lazy(() => import('./modules/inbox/InboxAnalytics'))
const HoursPage = lazy(() => import('./modules/hours/HoursPage'))
const HoursReportPage = lazy(() => import('./modules/hours/HoursReportPage'))
const AnimalsPage = lazy(() => import('./modules/animals/AnimalsPage'))
const AnimalProfilePage = lazy(() => import('./modules/animals/AnimalProfilePage'))
const NewAnimalPage = lazy(() => import('./modules/animals/NewAnimalPage'))
const EditAnimalPage = lazy(() => import('./modules/animals/EditAnimalPage'))
const FostersPage = lazy(() => import('./modules/animals/FostersPage'))
const FinancesPage = lazy(() => import('./modules/finances/FinancesPage'))
const NewTransactionPage = lazy(() => import('./modules/finances/NewTransactionPage'))
const InventoryPage = lazy(() => import('./modules/inventory/InventoryPage'))
const CheckoutPage = lazy(() => import('./modules/inventory/CheckoutPage'))
const ReturnsPage = lazy(() => import('./modules/inventory/ReturnsPage'))
const InventoryImportPage = lazy(() => import('./modules/inventory/InventoryImportPage'))
const AgreementsPage = lazy(() => import('./modules/agreements/AgreementsPage'))
const NewAgreementPage = lazy(() => import('./modules/agreements/NewAgreementPage'))
const ChatPage = lazy(() => import('./modules/chat/ChatPage'))
const BoardPage = lazy(() => import('./modules/board/BoardPage'))
const MeetingsPage = lazy(() => import('./modules/meetings/MeetingsPage'))
const CalendarPage = lazy(() => import('./modules/calendar/CalendarPage'))
const DocumentsPage = lazy(() => import('./modules/documents/DocumentsPage'))
const SupportPage = lazy(() => import('./modules/support/SupportPage'))
const SupportAdminPage = lazy(() => import('./modules/support/SupportAdminPage'))
const SafetyAlertsPage = lazy(() => import('./modules/safety/SafetyAlertsPage'))
const SettingsPage = lazy(() => import('./modules/settings/SettingsPage'))
const TeamPage = lazy(() => import('./modules/settings/TeamPage'))
const FosterProfile = lazy(() => import('./modules/settings/FosterProfile'))
const SocialStatsPage = lazy(() => import('./modules/settings/SocialStatsPage'))
const NotificationsPage = lazy(() => import('./modules/settings/NotificationsPage'))
const NotificationPrefsPage = lazy(() => import('./modules/settings/NotificationPrefsPage'))
const NewsletterPage = lazy(() => import('./modules/reports/NewsletterPage'))
const DonorsPage = lazy(() => import('./modules/donors/DonorsPage'))
const DonorDetailPage = lazy(() => import('./modules/donors/DonorDetailPage'))
const DonorImportPage = lazy(() => import('./modules/donors/DonorImportPage'))
const CanadaHelpsImport = lazy(() => import('./modules/donors/CanadaHelpsImport'))
const AdoptersPage = lazy(() => import('./modules/adopters/AdoptersPage'))
const AdopterDetailPage = lazy(() => import('./modules/adopters/AdopterDetailPage'))
const EmailCallbackPage = lazy(() => import('./modules/settings/EmailCallbackPage'))
const QBOCallbackPage = lazy(() => import('./modules/settings/QBOCallbackPage'))
const PlatformDashboard = lazy(() => import('./modules/platform/PlatformDashboard'))
const OrgsListPage = lazy(() => import('./modules/platform/OrgsListPage'))
const OrgDetailPage = lazy(() => import('./modules/platform/OrgDetailPage'))
const UsersListPage = lazy(() => import('./modules/platform/UsersListPage'))

class AppErrorBoundary extends Component {
  state = { error: null }
  static getDerivedStateFromError(error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center">
          <div>
            <h1 className="text-xl font-semibold text-on-surface mb-2">Something went wrong</h1>
            <p className="text-sm text-on-surface-variant mb-4 max-w-md">{this.state.error?.message}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
              className="px-4 py-2 rounded-md text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #004b4b 0%, #006565 100%)' }}
            >
              Reload app
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

function ProtectedRoute({ children }) {
  const { user, loading, profile, needsPasswordSetup } = useAuth()
  if (loading) return <PageLoading />
  if (!user) {
    // If the user was just deactivated (auth.jsx set the flag before
    // signOut), route them to the explainer page rather than /login —
    // silent login loops cause password-reset attempts that can't help.
    let wasDeactivated = false
    try { wasDeactivated = sessionStorage.getItem('placed:deactivated') === '1' } catch { /* noop */ }
    if (wasDeactivated) return <Navigate to="/deactivated" replace />
    return <Navigate to="/login" replace />
  }
  if (needsPasswordSetup) return <Navigate to="/set-password" replace />
  // Adopters have no business inside AppShell — shunt them to their
  // own dashboard. Prevents a signed-in adopter from rendering the
  // staff nav on direct hits to / or any other protected route.
  if (profile?.role === 'adopter') return <Navigate to="/dashboard/applications" replace />
  if (!profile) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-xl font-semibold tracking-[0.05em] text-on-surface mb-2">No Profile Found</h1>
          <p className="text-sm text-on-surface-variant">Contact your organization admin to get access.</p>
        </div>
      </div>
    )
  }
  return children
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function SuperAdminRoute({ children }) {
  // Uses realIsSuperAdmin so that a super_admin previewing as admin/member/foster
  // can still access platform routes via the Exit Preview button.
  const { realIsSuperAdmin, isSuperAdmin } = useRole()
  if (!realIsSuperAdmin && !isSuperAdmin) return <Navigate to="/" replace />
  return children
}

function StaffOnlyRoute({ children }) {
  // Blocks foster role (including super_admin previewing as foster) from
  // finance/inventory/agreements/settings/etc.
  const { isFoster } = useRole()
  if (isFoster) return <Navigate to="/" replace />
  return children
}

function AdminRoute({ children }) {
  // Admin-only screens (donors, safety alerts, social stats, create
  // agreement/transaction). Members are redirected. Must be route-level
  // so that if the super_admin preview role switcher flips isAdmin mid-
  // session, the component unmounts/remounts cleanly — no Rules-of-Hooks
  // violation from an in-component `<Navigate/>` between hook calls.
  const { isAdmin } = useRole()
  if (!isAdmin) return <Navigate to="/" replace />
  return children
}

function LoginRedirect() {
  // Post-login redirect target: staff/admin → /, adopters → /dashboard.
  // Covers first-time sign-ins where the role is only known after
  // fetchProfile runs; if profile is still loading we send to / and
  // ProtectedRoute will re-route adopters one hop further.
  const { profile } = useAuth()
  if (profile?.role === 'adopter') return <Navigate to="/dashboard/applications" replace />
  return <Navigate to="/" replace />
}

function AdopterRoute({ children }) {
  // Gate for /dashboard/* adopter-facing routes.
  //
  // Accepts three states:
  //   1. Authenticated with profile.role === 'adopter' → render.
  //   2. Authenticated with NO profile (mid-claim flow, profile row
  //      not yet created) → render. The ConfirmClaimPage + dashboard
  //      work with a minimal session; the profile is created by the
  //      claim RPC and refetched before navigation.
  //   3. Authenticated staff (admin/member/foster/super_admin) visiting
  //      /dashboard/* → redirect to the main app since this shell
  //      isn't for them.
  //
  // Unauthenticated → /login.
  const { user, loading, profile } = useAuth()
  if (loading) return <PageLoading />
  if (!user) return <Navigate to="/login" replace />
  if (profile && profile.role !== 'adopter') return <Navigate to="/" replace />
  return children
}

function AppRoutes() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoading />

  return (
    <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route path="/login" element={user ? <LoginRedirect /> : <LoginPage />} />
      <Route path="/set-password" element={user ? <SetPasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/deactivated" element={<DeactivatedPage />} />

      {/* Public routes — NO authentication required */}
      <Route element={<PublicShell />}>
        <Route path="/adopt/:orgSlug" element={<AdoptListPage />} />
        <Route path="/adopt/:orgSlug/:animalSlug" element={<AdoptAnimalPage />} />
        <Route path="/adopt/:orgSlug/:animalSlug/apply" element={<AdoptionApplicationPage />} />
        <Route path="/adopt/:orgSlug/:animalSlug/apply/success" element={<AdoptionSuccessPage />} />
        <Route path="/applications/claim" element={<ClaimApplicationPage />} />
        <Route path="/applications/claim/confirm" element={<ConfirmClaimPage />} />
        <Route path="/foster/:orgSlug/apply" element={<FosterApplicationPage />} />
      </Route>

      {/* Adopter-facing dashboard — authenticated, role='adopter' */}
      <Route element={<AdopterRoute><AdopterShell /></AdopterRoute>}>
        <Route path="/dashboard/applications" element={<DashboardApplicationsPage />} />
        <Route path="/dashboard/applications/:id" element={<DashboardApplicationDetailPage />} />
        <Route path="/dashboard/settings" element={<DashboardSettingsPage />} />
      </Route>

      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="grants" element={<Navigate to="/finances?tab=grants" replace />} />
        <Route path="animals" element={<AnimalsPage />} />
        <Route path="animals/:id" element={<AnimalProfilePage />} />
        <Route path="animals/:id/edit" element={<StaffOnlyRoute><EditAnimalPage /></StaffOnlyRoute>} />
        <Route path="animals/new" element={<StaffOnlyRoute><NewAnimalPage /></StaffOnlyRoute>} />
        <Route path="animals/fosters" element={<StaffOnlyRoute><FostersPage /></StaffOnlyRoute>} />
        <Route path="hours" element={<StaffOnlyRoute><HoursPage /></StaffOnlyRoute>} />
        <Route path="hours/report" element={<StaffOnlyRoute><HoursReportPage /></StaffOnlyRoute>} />
        <Route path="finances" element={<StaffOnlyRoute><FinancesPage /></StaffOnlyRoute>} />
        <Route path="finances/new" element={<AdminRoute><NewTransactionPage /></AdminRoute>} />
        <Route path="inventory" element={<StaffOnlyRoute><InventoryPage /></StaffOnlyRoute>} />
        <Route path="inventory/checkout" element={<StaffOnlyRoute><CheckoutPage /></StaffOnlyRoute>} />
        <Route path="inventory/returns" element={<StaffOnlyRoute><ReturnsPage /></StaffOnlyRoute>} />
        <Route path="inventory/import" element={<AdminRoute><InventoryImportPage /></AdminRoute>} />
        <Route path="agreements" element={<StaffOnlyRoute><AgreementsPage /></StaffOnlyRoute>} />
        <Route path="agreements/new" element={<AdminRoute><NewAgreementPage /></AdminRoute>} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="board" element={<StaffOnlyRoute><BoardPage /></StaffOnlyRoute>} />
        <Route path="meetings" element={<Navigate to="/calendar" replace />} />
        <Route path="calendar" element={<StaffOnlyRoute><CalendarPage /></StaffOnlyRoute>} />
        <Route path="documents" element={<StaffOnlyRoute><DocumentsPage /></StaffOnlyRoute>} />
        <Route path="applications" element={<StaffOnlyRoute><ApplicationsPage /></StaffOnlyRoute>} />
        <Route path="applications/adoption/:id" element={<StaffOnlyRoute><AdoptionApplicationDetailPage /></StaffOnlyRoute>} />
        <Route path="applications/:id" element={<StaffOnlyRoute><ApplicationDetailPage /></StaffOnlyRoute>} />
        <Route path="inbox" element={<StaffOnlyRoute><InboxPage /></StaffOnlyRoute>} />
        <Route path="inbox/analytics" element={<StaffOnlyRoute><InboxAnalytics /></StaffOnlyRoute>} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support/admin" element={<StaffOnlyRoute><SupportAdminPage /></StaffOnlyRoute>} />
        <Route path="safety-alerts" element={<AdminRoute><SafetyAlertsPage /></AdminRoute>} />
        <Route path="settings" element={<StaffOnlyRoute><SettingsPage /></StaffOnlyRoute>} />
        <Route path="settings/team" element={<StaffOnlyRoute><TeamPage /></StaffOnlyRoute>} />
        <Route path="settings/qbo-callback" element={<StaffOnlyRoute><QBOCallbackPage /></StaffOnlyRoute>} />
        <Route path="settings/email-callback" element={<StaffOnlyRoute><EmailCallbackPage /></StaffOnlyRoute>} />
        <Route path="settings/social-stats" element={<AdminRoute><SocialStatsPage /></AdminRoute>} />
        <Route path="reports/newsletter" element={<StaffOnlyRoute><NewsletterPage /></StaffOnlyRoute>} />
        <Route path="donors" element={<AdminRoute><DonorsPage /></AdminRoute>} />
        <Route path="donors/:id" element={<AdminRoute><DonorDetailPage /></AdminRoute>} />
        <Route path="donors/import" element={<AdminRoute><DonorImportPage /></AdminRoute>} />
        <Route path="donors/import/canadahelps" element={<AdminRoute><CanadaHelpsImport /></AdminRoute>} />
        <Route path="adopters" element={<StaffOnlyRoute><AdoptersPage /></StaffOnlyRoute>} />
        <Route path="adopters/:id" element={<StaffOnlyRoute><AdopterDetailPage /></StaffOnlyRoute>} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings/notifications" element={<NotificationPrefsPage />} />
        <Route path="me" element={<FosterProfile />} />
      </Route>

      <Route path="/platform" element={<ProtectedRoute><SuperAdminRoute><PlatformShell /></SuperAdminRoute></ProtectedRoute>}>
        <Route index element={<PlatformDashboard />} />
        <Route path="orgs" element={<OrgsListPage />} />
        <Route path="orgs/:id" element={<OrgDetailPage />} />
        <Route path="users" element={<UsersListPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <PreviewRoleProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </PreviewRoleProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  )
}
